import { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Calendar } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn, getLuxonLocale } from '@/lib/utils';

interface DateInputPickerProps {
  value?: string;
  onChange: (isoDate: string) => void;
  placeholder?: string;
  className?: string;
}

export function DateInputPicker({
  value,
  onChange,
  placeholder,
  className,
}: DateInputPickerProps) {
  const { locale } = useI18nContext();
  const [open, setOpen] = useState(false);

  const displayValue = useMemo(() => {
    if (!value) return '';

    const parsed = DateTime.fromISO(value).setLocale(getLuxonLocale(locale));
    return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : '';
  }, [locale, value]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <div className={cn('relative', className)}>
          <Calendar className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            readOnly
            value={displayValue}
            placeholder={placeholder}
            className="h-8 cursor-pointer pl-8"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <DatePicker
          value={value ? DateTime.fromISO(value).toJSDate() : undefined}
          onChange={(date) => {
            if (!date) return;
            onChange(DateTime.fromJSDate(date).toISODate() ?? '');
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
